// src/pages/ContactPage.tsx
import { useState, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import ScrollReveal from '../components/ScrollReveal'
import type { ContactFormData } from '../types'
import styles from './ContactPage.module.css'

const INITIAL_FORM: ContactFormData = {
  fname: '', lname: '', email: '', phone: '',
  business: '', venue: '', location: '', message: '', consent: false,
}

const VENUE_OPTIONS = [
  'Gym / Fitness Studio', 'Bar / Nightclub / Lounge', 'Office / Workplace',
  'Hotel / Hospitality', 'Retail / Shopping Centre', 'Salon / Spa',
  'Educational Institution', 'Recreational Facility', 'Other',
]

const CONTACT_ITEMS = [
  { icon: '📧', title: 'Email', value: 'Contact@freshdispense.com', href: 'mailto:Contact@freshdispense.com', sub: 'We respond within 24 hours' },
  { icon: '📞', title: 'Phone', value: '780-607-0170', href: 'tel:+17806070170', sub: 'Monday – Friday, 9am–5pm MST' },
  { icon: '📍', title: 'Location', value: 'Serving Edmonton & Alberta', href: null, sub: 'No fixed public address yet' },
  { icon: '📸', title: 'Instagram', value: '@freshdispense', href: 'https://instagram.com/freshdispense', sub: 'See our Edmonton placements in action' },
]

const HOURS = [
  { day: 'Monday – Friday', time: '9:00 AM – 5:00 PM', open: true },
  { day: 'Saturday', time: 'By appointment', open: false },
  { day: 'Sunday', time: 'Closed', open: false },
]

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  url: 'https://www.freshdispense.com/contact',
  name: 'Contact FreshDispense — Edmonton Vending Machine Placement',
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'FreshDispense',
    telephone: '+17806070170',
    email: 'Contact@freshdispense.com',
    address: { '@type': 'PostalAddress', addressLocality: 'Edmonton', addressRegion: 'AB', addressCountry: 'CA' },
  },
}

export default function ContactPage() {
  const [form, setForm] = useState<ContactFormData>(INITIAL_FORM)
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const CONTACT_ENDPOINT = 'https://email.nexfolyo.com/freshdispense/contact'

  function validate(): boolean {
    const e: typeof errors = {}
    if (!form.fname.trim()) e.fname = 'First name is required'
    if (!form.lname.trim()) e.lname = 'Last name is required'
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email is required'
    if (!form.business.trim()) e.business = 'Business name is required'
    if (!form.venue) e.venue = 'Please select a venue type'
    if (!form.message.trim()) e.message = 'Please tell us about your space'
    if (!form.consent) e.consent = 'Please agree to be contacted'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value, type } = e.target
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    setSubmitError('')

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })

      if (!response.ok) {
        throw new Error('Submit failed')
      }

      setSubmitted(true)
    } catch (error) {
      setSubmitError('Your message did not make it — we are working on it.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <SEOHead
        title="Contact FreshDispense | Vending Machine Placement Edmonton | Free Consultation"
        description="Contact FreshDispense, Edmonton's #1 vending machine placement company. Request a free consultation for your gym, bar, office, or venue in YEG, Alberta. We respond within 24 hours."
        canonical="/contact"
        keywords="contact FreshDispense, vending machine Edmonton contact, free vending consultation Edmonton, YEG vending company contact"
        schema={CONTACT_SCHEMA}
      />

      <main className={`page-enter ${styles.main}`}>
        {/* ─── PAGE HERO ─── */}
        <section className={styles.pageHero} aria-label="Contact header">
          <div className="container">
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <Link to="/">Home</Link><span className={styles.breadSep}>/</span><span>Contact</span>
            </nav>
            <ScrollReveal>
              <span className="badge badge-blue"><span className="badge-dot" />Free Consultation Available</span>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <h1 className={styles.pageH1}>
                Let's Talk Vending<br />
                <span className="gradient-text">in Edmonton</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p className={styles.pageSubtitle}>
                Ready to add a vending machine to your Edmonton business? Book a free consultation
                and we'll find the perfect placement — at zero cost to you.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* ─── WHY CONTACT ─── */}
        <section className="section-sm" aria-label="Why contact us">
          <div className="container">
            <div className={styles.whyGrid}>
              {[
                { icon: '⚡', title: 'Fast Response', desc: 'We reply to every Edmonton inquiry within 24 hours — usually much sooner.' },
                { icon: '💰', title: 'Zero Cost to You', desc: 'Free placement consultation. No commitment required.' },
                { icon: '🤝', title: 'Local Edmonton Team', desc: "You're talking to real YEG people — not a call centre." },
              ].map((w, i) => (
                <ScrollReveal key={w.title} delay={i * 100}>
                  <div className={styles.whyCard}>
                    <div style={{ fontSize: 36, marginBottom: '1rem' }}>{w.icon}</div>
                    <h4 style={{ fontSize: 16, color: 'var(--text)', marginBottom: '0.5rem' }}>{w.title}</h4>
                    <p style={{ fontSize: 13 }}>{w.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── MAIN CONTACT ─── */}
        <section className={styles.contactMain} aria-label="Contact form and info">
          <div className="container">
            <div className={styles.contactLayout}>
              {/* INFO COL */}
              <div>
                <ScrollReveal><span className="label-tag">Reach Us</span></ScrollReveal>
                <ScrollReveal delay={100}>
                  <h2 style={{ fontSize: 'clamp(26px,3.5vw,44px)', marginBottom: '1rem' }}>
                    Contact FreshDispense<br />Edmonton
                  </h2>
                </ScrollReveal>
                <ScrollReveal delay={200}>
                  <p style={{ fontSize: 16, lineHeight: 1.8, marginBottom: '2rem' }}>
                    Whether you want to get a machine placed, ask a question, or just learn more
                    about how we work — we'd love to hear from you.
                  </p>
                </ScrollReveal>

                <div className={styles.contactCards}>
                  {CONTACT_ITEMS.map((item, i) => (
                    <ScrollReveal key={item.title} delay={i * 80}>
                      <div className={styles.contactCard}>
                        <div className={styles.contactCardIcon}>{item.icon}</div>
                        <div>
                          <div className={styles.contactCardTitle}>{item.title}</div>
                          {item.href ? (
                            <a href={item.href} className={styles.contactCardValue}
                               target={item.href.startsWith('http') ? '_blank' : undefined}
                               rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}>
                              {item.value}
                            </a>
                          ) : (
                            <span className={styles.contactCardValue}>{item.value}</span>
                          )}
                          <div className={styles.contactCardSub}>{item.sub}</div>
                        </div>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>

                <ScrollReveal delay={200}>
                  <div className={styles.hoursBlock}>
                    <h4 className={styles.hoursTitle}>Office Hours</h4>
                    {HOURS.map(h => (
                      <div key={h.day} className={styles.hoursRow}>
                        <span className={styles.hoursDay}>{h.day}</span>
                        <span className={styles.hoursTime}>
                          {h.time}
                          {h.open && <span className={styles.openBadge}>OPEN</span>}
                        </span>
                      </div>
                    ))}
                    <div className={styles.hoursRow}>
                      <span className={styles.hoursDay}>Response time</span>
                      <span className={styles.hoursTime}>Within 24 hours</span>
                    </div>
                  </div>
                </ScrollReveal>
              </div>

              {/* FORM COL */}
              <ScrollReveal delay={100}>
                <div className={styles.formWrap}>
                  {submitted ? (
                    <div className={styles.successState}>
                      <div style={{ fontSize: 60, marginBottom: '1rem' }}>✅</div>
                      <h3 className={styles.successTitle}>Request Sent!</h3>
                      <p style={{ fontSize: 16 }}>
                        Thanks for reaching out to FreshDispense. Our Edmonton team will be in touch
                        within 24 hours to discuss your vending placement.
                      </p>
                      <Link to="/services" className="btn btn-outline" style={{ marginTop: '2rem' }}>
                        Browse Our Services →
                      </Link>
                    </div>
                  ) : (
                    <>
                      <h3 className={styles.formTitle}>Request Your Placement</h3>
                      <p style={{ fontSize: 14, marginBottom: '2rem' }}>
                        Fill out the form and we'll get back to you within 24 hours to discuss your Edmonton location.
                      </p>
                      <form onSubmit={handleSubmit} noValidate>
                        <div className={styles.formRow}>
                          <div className={styles.formGroup}>
                            <label htmlFor="fname">First Name *</label>
                            <input id="fname" name="fname" type="text" placeholder="Jane"
                              value={form.fname} onChange={handleChange} autoComplete="given-name" />
                            {errors.fname && <span className={styles.fieldError}>{errors.fname}</span>}
                          </div>
                          <div className={styles.formGroup}>
                            <label htmlFor="lname">Last Name *</label>
                            <input id="lname" name="lname" type="text" placeholder="Smith"
                              value={form.lname} onChange={handleChange} autoComplete="family-name" />
                            {errors.lname && <span className={styles.fieldError}>{errors.lname}</span>}
                          </div>
                        </div>
                        <div className={styles.formGroup}>
                          <label htmlFor="email">Email Address *</label>
                          <input id="email" name="email" type="email" placeholder="jane@yourbusiness.com"
                            value={form.email} onChange={handleChange} autoComplete="email" />
                          {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
                        </div>
                        <div className={styles.formGroup}>
                          <label htmlFor="phone">Phone Number</label>
                          <input id="phone" name="phone" type="tel" placeholder="+1 (780) 000-0000"
                            value={form.phone} onChange={handleChange} autoComplete="tel" />
                        </div>
                        <div className={styles.formGroup}>
                          <label htmlFor="business">Business Name *</label>
                          <input id="business" name="business" type="text" placeholder="Your Gym / Bar / Business"
                            value={form.business} onChange={handleChange} />
                          {errors.business && <span className={styles.fieldError}>{errors.business}</span>}
                        </div>
                        <div className={styles.formGroup}>
                          <label htmlFor="venue">Venue Type *</label>
                          <select id="venue" name="venue" value={form.venue} onChange={handleChange}>
                            <option value="" disabled>Select your venue type…</option>
                            {VENUE_OPTIONS.map(v => <option key={v}>{v}</option>)}
                          </select>
                          {errors.venue && <span className={styles.fieldError}>{errors.venue}</span>}
                        </div>
                        <div className={styles.formGroup}>
                          <label htmlFor="location">Location in Edmonton</label>
                          <input id="location" name="location" type="text"
                            placeholder="e.g. West Edmonton, Whyte Ave, Sherwood Park…"
                            value={form.location} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                          <label htmlFor="message">Tell Us About Your Space *</label>
                          <textarea id="message" name="message"
                            placeholder="Describe your venue, foot traffic, and what type of products you think would work for your customers…"
                            value={form.message} onChange={handleChange} />
                          {errors.message && <span className={styles.fieldError}>{errors.message}</span>}
                        </div>
                        <div className={styles.consentRow}>
                          <input type="checkbox" id="consent" name="consent"
                            checked={form.consent} onChange={handleChange} />
                          <label htmlFor="consent" className={styles.consentLabel}>
                            I agree to be contacted by FreshDispense regarding my vending placement inquiry.
                            We respect your privacy and will never share your information.
                          </label>
                        </div>
                        {errors.consent && <span className={styles.fieldError}>{errors.consent}</span>}
                        <button
                          type="submit"
                          className={styles.submitBtn}
                          disabled={submitting}
                        >
                          {submitting ? 'Sending…' : 'Send My Request →'}
                        </button>
                        {submitError && <div className={styles.submitError}>{submitError}</div>}
                        <p className={styles.formNote}>🔒 Your information is secure. We respond within 24 hours.</p>
                      </form>
                    </>
                  )}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* ─── DIRECT CONTACT ─── */}
        <section className={styles.directSection} aria-label="Direct contact">
          <div className="container" style={{ textAlign: 'center' }}>
            <ScrollReveal>
              <h3 style={{ fontSize: 28, marginBottom: '0.75rem' }}>Prefer to Reach Us Directly?</h3>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <p style={{ maxWidth: 480, margin: '0 auto 2rem' }}>
                No form required. Call or email us directly and an Edmonton team member will get back to you fast.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="tel:+17806070170" className="btn btn-primary">📞 Call Us: 780-607-0170</a>
                <a href="mailto:Contact@freshdispense.com" className="btn btn-outline">✉️ Email Us</a>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  )
}
